import React, { useState, useEffect } from 'react';
import axios from 'axios';
import params from '../config/Params';
import { getItemFromLocalStorage } from '../utils/storageUtils';
import { toast } from 'react-toastify';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import StarButton from './StarButton';
import LazyLoad from 'react-lazyload';
import Loader from './Loader';
import LoadingDots from './LoadingDots';
import DataLoader from './DataLoader';

const ItemType = 'IMAGE';

const ImageItem = ({ image, index, imageId, moveImage, removeImage, isCover, setAsCover, saveAsCoverWithId, globalLoading = false }) => {
  const [, ref] = useDrag({
    type: ItemType,
    item: { index },
  });

  const [, drop] = useDrop({
    accept: ItemType,
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveImage(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div ref={(node) => ref(drop(node))} className='image-preview col-md-6'>
      <div className='position-relative'>
        <img src={image.pic || image.preview} alt={`preview-${index}`} className='preview-img' />
        <button type='button' className='remove-image-btn' onClick={() => removeImage(index)}>
          <i class="fas fa-minus"></i>
        </button>
        {isCover ? (
          <span className='cover-photo-label'>Cover Photo</span>
        ) : (
          <button className='set-cover-btn' id={imageId} onClick={() => { setAsCover(index); saveAsCoverWithId(imageId); }}>
            Set as Cover
          </button>
        )}
        {globalLoading && image.file && (
          <div className="overlay-loader">
            <DataLoader />
          </div>
        )}
      </div>
    </div>
  );
};

const ListingStep8 = ({ formData, handleChange, prevStep, onRegisterSave }) => {
  const { listingId } = formData;
  const [listingData, setListingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [coverPhotoIndex, setCoverPhotoIndex] = useState(0);
  const [question, setQuestion] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [nextLoading, setNextLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file, index) => ({
      file,
      preview: URL.createObjectURL(file),
      id: `img${images.length + index + 1}`
    }));

    setImages((prevImages) => {
      const updatedImages = [...prevImages, ...newImages];
      if (prevImages.length === 0 && newImages.length > 0) {
        setCoverPhotoIndex(0);
      }
      return updatedImages;
    });
  };

  const moveImage = (fromIndex, toIndex) => {
    const updatedImages = [...images];
    const [movedImage] = updatedImages.splice(fromIndex, 1);
    updatedImages.splice(toIndex, 0, movedImage);
    setImages(updatedImages);

    if (coverPhotoIndex === fromIndex) {
      setCoverPhotoIndex(toIndex);
    } else if (coverPhotoIndex === toIndex) {
      setCoverPhotoIndex(fromIndex);
    }
  };

  const removeImage = async (index, photo_id) => {
    try {
      const updatedImages = images.filter((_, i) => i !== index);

      const hostData = getItemFromLocalStorage('hostProfile');
      if (!hostData || !hostData.host_access_token) {
        console.error('Host access token not found.');
        return;
      }

      let formData = new FormData();
      formData.append('token', hostData.host_access_token);
      formData.append('listing_id', listingId);
      formData.append('id', photo_id);

      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        url: `${params.baseURL}${params.endpoints.removePhoto}`,
        data: formData,
      });

      if (response.data.status === 'done') {
        console.log('Photo successfully deleted on server.');
        setImages(updatedImages);

        if (index === coverPhotoIndex) {
          setCoverPhotoIndex(0);
        } else if (index < coverPhotoIndex) {
          setCoverPhotoIndex((prev) => prev - 1);
        }
      } else {
        console.error('Unable to delete your room. Please try again later.');
      }
    } catch (error) {
      console.error('Error removing image:', error.message);
    }
  };

  const setAsCover = (index) => {
    setCoverPhotoIndex(index);
  };

  useEffect(() => {
    const fetchListingData = async () => {
      const hostData = getItemFromLocalStorage('hostProfile');
      let formData = new FormData();
      formData.append('token', hostData.host_access_token);
      formData.append('listing_id', listingId);

      setLoading(true);

      try {
        const response = await axios({
          method: 'post',
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          url: `${params.baseURL}${params.endpoints.getListingData}`,
          data: formData,
        });

        if (response.data.status === 'ok') {
          const listingData = response.data.updated_property_item;
          const { appr_list, resrv_list, offers, place_options_list } = listingData;
          setListingData(response.data.updated_property_item);
          setQuestion(response.data.updated_property_item.questions);

          const { photos = [], cover_photo } = listingData;

          const photoData = photos.map((photo) => ({
            id: photo.stay_listing_photo_id,
            pic: photo.pic,
            isCover: photo.is_cover === 1,
          }));

          setImages(photoData);
          const coverIndex = photoData.findIndex((photo) => photo.isCover);
          if (coverIndex !== -1) setCoverPhotoIndex(coverIndex);
          setLoading(false);
        } else {
          toast.error('An error occurred while fetching data.');
          setLoading(false);
        }
      } catch (err) {
        console.log(err.message);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchListingData();
  }, [listingId]);


  const saveListing = async () => {

    const hostData = getItemFromLocalStorage('hostProfile');
    const listing_id = listingId;

    const formData = new FormData();
    formData.append('token', hostData.host_access_token);
    formData.append('listing_id', listing_id);

    const existingImageIds = [];

    setNextLoading(true);
    setImageLoading(true);

    //console.log(images);

    images.forEach((image, index) => {
      if (image.file) {
        formData.append(image.id, image.file);
      } else {
        existingImageIds.push(image.id);
      }
    });

    if (existingImageIds.length > 0) {
      formData.append('ids', existingImageIds.join(','));
    }

    try {
      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        url: `${params.baseURL}${params.endpoints.updateImages}`,
        data: formData,
      });

      if (response.data.status === 'completed') {
        //await saveAsCover(coverPhotoIndex);
        const photos = response.data.updated_property_item.photos;
        let coverPhotoSet = false;
        let firstPhotoId = null;
        let coverPhotoFromServer = null;
        //console.log("Photos:", photos);

        if (photos && photos.length > 0) {
          photos.forEach((photo) => {
            if (photo.is_cover === 1) {
              coverPhotoSet = true;
              coverPhotoFromServer = photo.stay_listing_photo_id;
            }
            if (firstPhotoId === null) {
              firstPhotoId = photo.stay_listing_photo_id;
            }
          });

          if (coverPhotoSet) {
            if (coverPhotoIndex !== null && images[coverPhotoIndex] && images[coverPhotoIndex].id === coverPhotoFromServer) {
              console.log("coverPhotoSet", coverPhotoSet);
            } else {
              const indexToSet = images.findIndex(img => img.id === coverPhotoFromServer);
              if (indexToSet !== -1) {
                saveAsCover(indexToSet);
              }
            }
          } else {
            if (firstPhotoId !== null) {
              saveAsCoverWithId(firstPhotoId);
            }
          }
        } else {
          if (coverPhotoIndex !== null && images[coverPhotoIndex]) {
            saveAsCover(coverPhotoIndex);
          }
        }
        return true;
      } else if (response.data.status === 'error') {
        response.data.list.forEach((errorMsg) => {
          toast.error(errorMsg);
        });
        return false;
      } else {
        toast.error('Failed to save data');
        setNextLoading(false);
        setImageLoading(false);
        return false;
      }
    } catch (err) {
      toast.error('An error occurred while saving the listing');
      setNextLoading(false);
      setImageLoading(false);
      return false;
    }
  };



  const saveAsCover = async (index) => {
    const hostData = getItemFromLocalStorage('hostProfile');
    const imageId = images[index].id;
    console.log(index);
    console.log(imageId);

    const formData = new FormData();
    formData.append('token', hostData.host_access_token);
    formData.append('id', imageId);

    try {
      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        url: `${params.baseURL}${params.endpoints.makeItCover}`,
        data: formData,
      });

      if (response.data.status === 'completed') {
        console.log('Cover photo updated successfully');
      } else {
        console.log('Failed to update cover photo');
      }
    } catch (error) {
      console.log('An error occurred while updating the cover photo');
    }
  };

  const saveAsCoverWithId = async (index) => {
    const hostData = getItemFromLocalStorage('hostProfile');

    const formData = new FormData();
    formData.append('token', hostData.host_access_token);
    formData.append('id', index);

    try {
      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        url: `${params.baseURL}${params.endpoints.makeItCover}`,
        data: formData,
      });

      if (response.data.status === 'completed') {
        console.log('Cover photo updated successfully');
      } else {
        console.log('Failed to update cover photo');
      }
    } catch (error) {
      console.log('An error occurred while updating the cover photo');
    }
  };

  if (loading) {
    return <Loader />;
  }

  const faqs = [
    {
      question: "What is Lorem Ipsum?",
      answer: "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book."
    },
    {
      question: "Where does it come from?",
      answer: "Contrary to popular belief, Lorem Ipsum is not simply random text. It has roots in a piece of classical Latin literature from 45 BC, making it over 2000 years old."
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <button id='host-listing-trigger-8' style={{ display: 'none' }} onClick={() => {
        if (onRegisterSave) {
          onRegisterSave(saveListing);
        }
      }}>Done</button>
      <div className="fade-in"> {/*clss */}
        <div className='row'>
          {/*<div className='col-md-4 listing-img-box'>
            <div className='lsib'>
              <img src={'/assets/img/listing/listing_side_img.gif'} alt="Listing Image" className="listing-image" />
              {question && (
                <div className='lsqa-btn'>
                  <StarButton text="Guide" link={question.find((q) => q.id === 8)?.link || '#'} />
                </div>
              )}
            </div>
          </div>*/}

          <div className='col-md-7'>

            <div className='ws-cl-header' style={{ padding: '0 12px' }}>
              <div>
                <h4 className='ws-title'>Upload Property Photos</h4>
                <p className='ws-desc'>Minimum 5 and maximum 100 photos required. Photos can be updated anytime later.</p>
              </div>
            </div>

            <div className='st1-desc-box position-relative'>
              <div className='row'>
                <div className='col-md-12'>
                  {images.length > 0 && (
                    <div className='cover-photo-box'>
                      <h6 className='cp-label'>Cover Photo</h6>
                      <LazyLoad
                        height={460}
                        offset={460}
                        placeholder={<div className="img-placeholder"><div className="spinner"></div></div>}
                        once
                      >
                        <img
                          src={images[coverPhotoIndex].preview ? images[coverPhotoIndex].preview : images[coverPhotoIndex].pic}
                          alt="cover-photo"
                          className="cover-photo-preview"
                        />
                      </LazyLoad>
                    </div>
                  )}
                </div>
              </div>

              <div className='image-upload-container'>
                <div className="row image-preview-container position-relative">
                  {images.map((image, index) => (
                    <ImageItem
                      key={index}
                      index={index}
                      image={image}
                      imageId={image.id}
                      moveImage={moveImage}
                      removeImage={() => removeImage(index, image.id)}
                      isCover={index === coverPhotoIndex}
                      setAsCover={setAsCover}
                      saveAsCoverWithId={saveAsCoverWithId}
                      globalLoading={imageLoading}
                    />
                  ))}
                  <div className='col-md-6'>
                    <label htmlFor="file" className="custum-file-upload">
                      <div className="icon">
                        <div className="text">
                          <span><i className="fas fa-plus"></i></span>
                        </div>
                        <input id="file" type="file" accept="image/*" multiple onChange={handleImageChange} />
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

          </div>
          <div className='col-md-1'></div>
          <div className='col-md-4 p-0'>
            <div className='bfhsd'>
              <div className='blog-box'>
                <div class="blog">
                  <div class="blog-image">
                    <img src={'https://app.wirestay.com/ws/uploads/blog/1736870810_pi.jpg'} className='img-fluid' alt="" />
                    <div class="date">May 08, 2025</div>
                  </div>
                  <div class="blog-content">
                    <h2>What is Lorem Ipsum?</h2>
                    <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Mauris vel metus vel est fermentum consectetur.</p>
                    <a href="#">Read More</a>
                  </div>
                </div>
              </div>
              <div className="faq-sticky">
                {faqs.map((faq, index) => (
                  <div key={index} className='faqs-list-section'>
                    <div
                      onClick={() => toggleAccordion(index)} className='faqs-qustn-box'
                      style={{
                        backgroundColor: activeIndex === index ? "#f7f7f7" : "#ffffff",
                      }}
                    >
                      {faq.question}
                      <i
                        className={`fas ${activeIndex === index ? "fa-chevron-up" : "fa-chevron-down"
                          }`}
                        style={{
                          transition: "transform 0.3s ease",
                        }}
                      ></i>
                    </div>
                    {activeIndex === index && (
                      <div className='faqs-ans-box'>
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </DndProvider>
  );
};

export default ListingStep8;
